import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowLeft, MessageSquareWarning, User, Mail, Clock, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useComplaint } from '../hooks';

export function ComplaintDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: complaint, isLoading, isError, error } = useComplaint(id!);

  const formatDate = (dateStr?: string) =>
    dateStr ? format(new Date(dateStr), 'yyyy-MM-dd HH:mm') : '-';

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !complaint) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Complaint not found'}</p>
        <Link to="/app/complaints">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToList')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/app/complaints">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back')}
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <MessageSquareWarning className="h-6 w-6" />
            <h1 className="text-2xl font-bold">{t('complaintDetails')}</h1>
          </div>
        </div>
      </div>

      {/* Complaint Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('complaintContent')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="whitespace-pre-line">{complaint.comment || '-'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* User Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('userInformation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm text-muted-foreground">{t('name')}</p>
              {complaint.user ? (
                <Link
                  to={`/app/users/${complaint.user.id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {complaint.user.name}
                </Link>
              ) : (
                <p className="font-medium">-</p>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('status.status')}</p>
              <Badge variant={complaint.user?.status === 'ACTIVE' ? 'default' : 'destructive'}>
                {complaint.user?.status || '-'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Client Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {t('clientInformation')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm text-muted-foreground">{t('name')}</p>
              {complaint.client ? (
                <Link
                  to={`/app/clients/${complaint.client.id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {complaint.client.name}
                </Link>
              ) : (
                <p className="font-medium">-</p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('email')}:</p>
              <span>{complaint.client?.email || '-'}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('timestamps')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
              <p className="font-medium">{formatDate(complaint.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
              <p className="font-medium">{formatDate(complaint.updatedAt)}</p>
            </div>
            {complaint.createdBy && (
              <div>
                <p className="text-sm text-muted-foreground">{t('createdBy')}</p>
                <Link
                  to={`/app/users/${complaint.createdBy.id}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {complaint.createdBy.name}
                </Link>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
